import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatCard from "@/components/StatCard";
import StatusBadge from "@/components/StatusBadge";
import {
  AlertTriangle, ShieldAlert, MessageSquareWarning, ScanLine,
  Activity, MapPin, GraduationCap, BadgeCheck, FileWarning,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const KQ = [
  { key: "safe", label: "Safe", color: "#0D7377" },
  { key: "effective", label: "Effective", color: "#10B981" },
  { key: "caring", label: "Caring", color: "#3B82F6" },
  { key: "responsive", label: "Responsive", color: "#F59E0B" },
  { key: "well_led", label: "Well-Led", color: "#6B21A8" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [stale, setStale] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/dashboard/compliance").then(({ data }) => setData(data)).catch((e) => setErr(e.response?.data?.detail || e.message));
    api.get("/dashboard/statutory-staleness").then(({ data }) => setStale(data)).catch(() => {});
  }, []);

  if (err) {
    return <div className="text-red-700 bg-red-50 border border-red-200 p-4 rounded-md" data-testid="dashboard-error">{String(err)}</div>;
  }
  if (!data) return <div className="text-slate-500" data-testid="dashboard-loading">Loading dashboard…</div>;
  const s = data.summary || {};
  const trend = data.trend_incidents_14d || [];

  return (
    <div className="space-y-6" data-testid="cqc-dashboard">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#0D7377]">CQC inspection readiness</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Compliance overview</h1>
          <p className="text-sm text-slate-600 mt-1">Evidence mapped to the Five Key Questions — Safe, Effective, Caring, Responsive, Well-Led.</p>
        </div>
        <button
          onClick={() => window.print()}
          className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 rounded-md text-sm font-medium"
          data-testid="dashboard-print-button"
        >
          Export / print
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        <StatCard testId="stat-open-incidents" label="Open incidents" value={s.open_incidents} hint={`${s.by_severity?.critical || 0} critical · ${s.by_severity?.serious || 0} serious`} icon={AlertTriangle} tone={s.open_incidents > 0 ? "warning" : "neutral"} />
        <StatCard testId="stat-open-safeguarding" label="Open safeguarding" value={s.open_safeguarding} hint="Restricted to management" icon={ShieldAlert} tone={s.open_safeguarding > 0 ? "danger" : "neutral"} />
        <StatCard testId="stat-open-complaints" label="Open complaints" value={s.open_complaints} hint={`${s.overdue_complaints} overdue`} icon={MessageSquareWarning} tone={s.overdue_complaints > 0 ? "danger" : "neutral"} />
        <StatCard testId="stat-overdue-risks" label="Overdue risk reviews" value={s.overdue_risks} icon={ScanLine} tone={s.overdue_risks > 0 ? "warning" : "neutral"} />
        <StatCard testId="stat-care-plan-reviews" label="Overdue care plans" value={s.overdue_care_plans} hint={s.care_plans_due_soon ? `${s.care_plans_due_soon} due in 28 days` : ""} icon={FileWarning} tone={s.overdue_care_plans > 0 ? "danger" : s.care_plans_due_soon > 0 ? "warning" : "neutral"} />
        <StatCard testId="stat-incomplete-profiles" label="Incomplete profiles" value={s.incomplete_profiles} hint="Tabs unfinished — click for list" icon={FileWarning} tone={s.incomplete_profiles > 0 ? "warning" : "success"} />
        <StatCard testId="stat-missed-visits" label="Missed visits (7d)" value={s.missed_visits_7d} icon={MapPin} tone={s.missed_visits_7d > 0 ? "danger" : "neutral"} />
        <StatCard testId="stat-short-visits" label="Short visits (7d)" value={s.short_visits_7d} icon={Activity} tone={s.short_visits_7d > 0 ? "warning" : "neutral"} />
        <StatCard testId="stat-training-pct" label="Training compliance" value={`${s.training_compliance_pct ?? 0}%`} icon={GraduationCap} tone={(s.training_compliance_pct ?? 0) >= 80 ? "success" : "warning"} />
        <StatCard testId="stat-dbs-renewals" label="DBS renewals (30d)" value={s.dbs_renewals_30d} icon={BadgeCheck} tone={s.dbs_renewals_30d > 0 ? "warning" : "neutral"} />
      </div>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 shadow-sm p-5 cc-glow card-print">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Incidents — last 14 days</h3>
            <StatusBadge status={trend.reduce((acc, t) => acc + t.incidents, 0) > 5 ? "in_progress" : "active"} label={trend.reduce((acc, t) => acc + t.incidents, 0) > 5 ? "Watch" : "Stable"} />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(13,115,119,0.08)" }} />
                <Bar dataKey="incidents" fill="#0D7377" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 cc-glow card-print" data-testid="five-key-questions">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Five key questions</h3>
          <ul className="space-y-2">
            {KQ.map((q) => {
              const v = data.five_key_questions?.[q.key] || {};
              const totals = Object.values(v).reduce((a, b) => a + (Number(b) || 0), 0);
              const tone = q.key === "safe" || q.key === "responsive"
                ? totals === 0 ? "active" : "in_progress"
                : "active";
              return (
                <li key={q.key} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-2.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: q.color }} />
                    <span className="text-sm font-medium text-slate-900">{q.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{Object.entries(v).map(([k, val]) => `${k.replace(/_/g, " ")}: ${val}`).join(" · ")}</span>
                    <StatusBadge status={tone} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {stale && (
        <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5" data-testid="statutory-staleness-widget">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-heading text-lg font-semibold text-slate-900">Statutory plan freshness</h3>
              <p className="text-xs text-slate-500">CPPCR 2010 Reg 35 (children) · Care Act 2014 s25 (adults)</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { key: "overdue_lac_review",       label: "Overdue LAC review (>180d)" },
              { key: "missing_iro_contact",      label: "Missing IRO contact" },
              { key: "missing_social_worker_phone", label: "Missing SW phone" },
              { key: "missing_care_act_plan_ref", label: "Missing Care Act s25 ref" },
              { key: "missing_lead_professional", label: "Missing lead professional" },
            ].map(({ key, label }) => {
              const n = stale.counts?.[key] || 0;
              return (
                <div key={key} className={`p-3 rounded-md border ${n > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid={`statutory-${key}`}>
                  <p className="text-[11px] uppercase tracking-wider text-slate-600">{label}</p>
                  <p className={`text-2xl font-bold ${n > 0 ? "text-amber-800" : "text-emerald-800"}`}>{n}</p>
                </div>
              );
            })}
          </div>
          {(stale.items?.overdue_lac_review || []).length > 0 && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-slate-700 font-medium" data-testid="statutory-staleness-details-toggle">Show service users to follow up ({stale.items.overdue_lac_review.length})</summary>
              <ul className="mt-2 divide-y divide-slate-100 max-h-52 overflow-y-auto">
                {stale.items.overdue_lac_review.map((su) => (
                  <li key={su.id} className="py-1.5 text-sm flex items-center justify-between gap-2">
                    <span className="text-slate-800">{su.full_name}{su.preferred_name ? ` "${su.preferred_name}"` : ""}</span>
                    <span className="text-xs text-amber-700">{su.last_review ? `${su.days_overdue}d since ${su.last_review}` : "no review on record"}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}
    </div>
  );
}
